/**
 * XlsxDropzone — a reusable drag-and-drop / click-to-choose picker for a
 * single .xlsx file (#17). Built on @mantine/dropzone. First used by the
 * Portfolio Data bulk-upload flow; intended to be reused by future intake
 * screens (AQ / Person PDF uploads, Documents).
 *
 * Accepts a single workbook by MIME or .xlsx extension (some valid xlsx files
 * report an empty/odd MIME, so the extension is matched too). The parent owns
 * what happens with the file (preview, etc.); rejections call onReject so the
 * parent can surface a friendly message.
 */
import { Group, Text } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconFileSpreadsheet, IconUpload, IconX } from '@tabler/icons-react';

/** MIME → allowed extensions (react-dropzone Accept shape). Matches a file
 *  whose MIME is the OOXML spreadsheet type OR whose name ends in .xlsx. */
const XLSX_ACCEPT = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

interface XlsxDropzoneProps {
  /** Called with the chosen file (the first, since multiple is off). */
  onSelect: (file: File) => void;
  /** Spinner + disabled while the parent processes the file. */
  loading?: boolean;
  /** Called when a drop is rejected (wrong type / more than one file). */
  onReject?: () => void;
  /** The line under the box naming the file THIS upload expects (DZ-TEXT, Pedro 2026-10-22:
   *  "give each upload its own hint line"). Required, so a new upload states its own file. */
  hint: string;
}

export function XlsxDropzone({ onSelect, loading, onReject, hint }: XlsxDropzoneProps) {
  return (
    <Dropzone
      onDrop={(files) => { if (files[0]) onSelect(files[0]); }}
      onReject={() => onReject?.()}
      accept={XLSX_ACCEPT}
      maxFiles={1}
      multiple={false}
      loading={loading}
    >
      <Group justify="center" gap="xl" mih={140} style={{ pointerEvents: 'none' }}>
        <Dropzone.Accept><IconUpload size={42} stroke={1.5} /></Dropzone.Accept>
        <Dropzone.Reject><IconX size={42} stroke={1.5} /></Dropzone.Reject>
        <Dropzone.Idle><IconFileSpreadsheet size={42} stroke={1.5} /></Dropzone.Idle>
        <div>
          <Text size="lg" inline>Drag the .xlsx workbook here, or click to choose</Text>
          <Text size="sm" c="dimmed" inline mt={7}>{hint}</Text>
        </div>
      </Group>
    </Dropzone>
  );
}
